import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { initializePaddle } from "@paddle/paddle-js";
import {
    ShieldCheck, Sparkle, Crown, Heart, Bell, MapPin, LockKey,
    ArrowRight, WarningCircle, PawPrint, QrCode, IdentificationCard, FirstAid, UsersThree,
    Siren, MapTrifold,
} from "@phosphor-icons/react";
import Nav from "@/components/Nav";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { track } from "@/lib/analytics";
import { trackInitiateCheckout, trackCompletePayment } from "@/lib/tiktokPixel";
import { SUPPORT_EMAIL } from "@/constants/support";
import { useIsStoreApp } from "@/lib/storeAppDetection";

const IMG_HERO = "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=1400&q=80";
const easeOut = [0.16, 1, 0.3, 1];

const tintMap = {
    rose:    "bg-rose-500/10 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300 ring-rose-500/25",
    emerald: "bg-emerald-500/10 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 ring-emerald-500/25",
    amber:   "bg-amber-500/10 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300 ring-amber-500/25",
    violet:  "bg-violet-500/10 dark:bg-violet-500/15 text-violet-600 dark:text-violet-300 ring-violet-500/25",
    sky:     "bg-sky-500/10 dark:bg-sky-500/15 text-sky-600 dark:text-sky-300 ring-sky-500/25",
    forest:  "bg-paw-forest/10 dark:bg-paw-terra/15 text-paw-forest dark:text-paw-terra ring-paw-forest/25 dark:ring-paw-terra/30",
};

export default function SubscriptionPage() {
    const { user, refresh } = useAuth();
    const { t, lang } = useI18n();
    const nav = useNavigate();
    const reduce = useReducedMotion();
    // Store-app compliance: hide the direct Paddle checkout inside TWA / iOS
    // WebView to avoid Google Play Payments policy + Apple 3.1.1 rejections.
    // Users get an "Open in browser to subscribe" fallback that hands them to
    // https://getpawlink.com/subscription in their real browser.
    const isStoreApp = useIsStoreApp();
    const isPremium = user?.plan === "premium";
    const [billing, setBilling] = useState("yearly");

    // GA4: fire a lightweight "Premium interest" event once per visit for
    // store-app users who open this page. Signals which Early Access users
    // are curious about a paid tier — informs the compliant monetization
    // roadmap without adding any purchase surface.
    useEffect(() => {
        if (isStoreApp && !isPremium) {
            track("premium_page_view", { source: "store_app_early_access" });
        }
    }, [isStoreApp, isPremium]);
    const isYearly = billing === "yearly";
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const paddleRef = useRef(null);

    // Config-guard state — if the production build is misconfigured (e.g. env=sandbox
    // or a test_ client token on getpawlink.com), we refuse to open Paddle and instead
    // show an internal-configuration banner so users never see the "Test Mode" badge.
    const [configError, setConfigError] = useState("");
    // CHF launch: when the new Paddle CHF price IDs are missing (staging state
    // before Paddle is reconfigured), disable paid checkout and show a clear
    // "Checkout awaiting Paddle CHF configuration" notice. Free flow stays alive.
    const [checkoutDisabledReason, setCheckoutDisabledReason] = useState("");
    // Safe browser-side diagnostics for on-page inspection. Never contains
    // the raw client token or secret material — only the 5-char TYPE prefix
    // (live_ / test_ / empty) and shape flags. Populated inside the init
    // useEffect below. Visible either when configError is set OR when the
    // URL includes ?debug=1 (so ops can inspect even a healthy production).
    const [diag, setDiag] = useState(null);
    // Deliberately not memoized — this is a diagnostic-only read.
    const debugParam = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1";

    // Initialize Paddle.js once. RUNTIME source of truth is /api/paddle/client-config
    // (which reads the backend env vars — always in sync with production). Falls
    // back to build-time REACT_APP_PADDLE_* if the endpoint fails, so preview/dev
    // still works. Paddle client tokens are explicitly safe to expose (per
    // developer.paddle.com/paddle-js/about#get-a-client-side-token).
    useEffect(() => {
        if (paddleRef.current) return;
        let cancelled = false;

        (async () => {
            // 1) Try runtime config from backend first. This is the AUTHORITATIVE
            //    source on production — it reads the same env vars validated by
            //    /api/paddle/config-health, which we already know are LIVE.
            let runtimeCfg = null;
            try {
                const { data } = await api.get("/paddle/client-config");
                if (data && data.client_token) {
                    runtimeCfg = data;
                }
            } catch (e) {
                // Endpoint missing (backend not redeployed yet) → fall back silently.
            }
            if (cancelled) return;

            // 2) Build-time fallback (preview, dev, or old backends that don't
            //    yet expose /paddle/client-config).
            //    CHF launch: only the *_CHF vars are consulted — legacy USD
            //    REACT_APP_PADDLE_PRICE_MONTHLY / _YEARLY are IGNORED so a
            //    stale build can never charge a CHF customer with a USD id.
            const buildToken = (process.env.REACT_APP_PADDLE_CLIENT_TOKEN || "").trim();
            const buildEnvRaw = (process.env.REACT_APP_PADDLE_ENV || "").trim().replace(/^["']|["']$/g, "").trim().toLowerCase();
            const buildMonthly = (process.env.REACT_APP_PADDLE_PRICE_MONTHLY_CHF || "").trim();
            const buildYearly = (process.env.REACT_APP_PADDLE_PRICE_YEARLY_CHF || "").trim();

            // 3) Resolve final effective config — runtime beats build-time when both present.
            const token = runtimeCfg?.client_token || buildToken;
            const rawEnv = runtimeCfg?.environment || buildEnvRaw;
            const monthlyId = runtimeCfg?.price_monthly || buildMonthly;
            const yearlyId = runtimeCfg?.price_yearly || buildYearly;
            const configSource = runtimeCfg ? "runtime_api" : "build_time_env";

            // STRICT PRODUCTION SAFETY GUARD
            // On the LIVE hostname we refuse to open Sandbox — either the runtime
            // API returned LIVE values or the frontend won't init. Preview/dev
            // hosts still permit sandbox.
            const host = (typeof window !== "undefined" && window.location.hostname) || "";
            const isLiveHost = host === "getpawlink.com" || host === "www.getpawlink.com";
            const envIsProduction = rawEnv === "production" || rawEnv === "live" || rawEnv === "prod";
            const tokenPrefix = token.slice(0, 5).toLowerCase();
            let tokenKind;
            if (!token) tokenKind = "missing";
            else if (tokenPrefix === "live_") tokenKind = "live";
            else if (tokenPrefix === "test_") tokenKind = "sandbox";
            else tokenKind = "unknown";

            // Snapshot ALL the safe diagnostics so we can render them even if init throws.
            // Also mirrored to window.__pawlink_paddle_diag for DevTools inspection.
            const initialDiag = {
                hostname: host,
                isLiveHost,
                paddleJsLoaded: typeof initializePaddle === "function",
                paddleInitialized: false,
                configSource,                        // "runtime_api" | "build_time_env"
                resolvedEnvironment: envIsProduction ? "production" : "sandbox",
                envVarRawFirst5: (rawEnv || "").slice(0, 10),
                envVarIsProduction: envIsProduction,
                clientTokenKind: tokenKind,
                clientTokenLength: token.length,
                monthlyPriceIdPrefix: monthlyId.slice(0, 4),
                yearlyPriceIdPrefix: yearlyId.slice(0, 4),
                monthlyPriceIdLength: monthlyId.length,
                yearlyPriceIdLength: yearlyId.length,
                paddleInitError: null,
                configError: "",
                timestamp: new Date().toISOString(),
            };
            setDiag(initialDiag);
            if (typeof window !== "undefined") window.__pawlink_paddle_diag = initialDiag;

            if (!token) {
                if (isLiveHost) setConfigError("checkout_config_missing_token");
                initialDiag.configError = isLiveHost ? "checkout_config_missing_token" : "";
                return;
            }

            // CHF launch — if the new Paddle CHF price IDs are missing on ANY
            // host, disable paid checkout and surface the staging notice.
            // Legacy USD price ids MUST NOT be shown to a CHF customer.
            if (!monthlyId || !yearlyId) {
                setCheckoutDisabledReason("checkout_awaiting_chf_configuration");
                initialDiag.configError = initialDiag.configError || "checkout_awaiting_chf_configuration";
            }

            if (isLiveHost) {
                if (!envIsProduction) {
                    setConfigError("checkout_config_wrong_env");
                    initialDiag.configError = "checkout_config_wrong_env";
                    return;
                }
                if (!token.startsWith("live_")) {
                    setConfigError("checkout_config_test_token");
                    initialDiag.configError = "checkout_config_test_token";
                    return;
                }
                if (!monthlyId || !yearlyId) {
                    setConfigError("checkout_config_missing_price");
                    initialDiag.configError = "checkout_config_missing_price";
                    return;
                }
            }

            // Persist the resolved config for startCheckout() to read (some fields
            // — like the monthly/yearly price IDs — are used inside the checkout call).
            paddleRef.current_config = { token, environment: envIsProduction ? "production" : "sandbox", monthlyId, yearlyId };

            const env = envIsProduction ? "production" : "sandbox";

        initializePaddle({
            token,
            environment: env,
            // Paddle Billing recommends setting default settings at init time so
            // EVERY checkout for this session inherits them. `allowLogout: false`
            // means the customer can NOT change/remove the email during checkout
            // — the email PawLink prefills stays locked. (Per Paddle docs,
            // developer.paddle.com/build/checkout/set-up-checkout-default-settings)
            checkout: {
                settings: {
                    displayMode: "overlay",
                    theme: "dark",
                    allowLogout: false,
                    // Explicit BCP-47 locale. Prevents Paddle from picking up
                    // a broken navigator.language (e.g. "en-US@posix" on some
                    // Linux/POSIX systems), which crashes the overlay with
                    // RangeError: Invalid language tag during Number.toLocaleString.
                    locale: (lang || "en").split("-")[0],
                    successUrl: `${window.location.origin}/home?paddle=success`,
                },
            },
            eventCallback: (evt) => {
                // Paddle raises checkout.completed once the payment succeeds inside the overlay.
                // Real activation still comes from the server webhook, but we can move the user
                // forward optimistically here. Small delay + refresh() ensures the webhook has
                // landed and users.plan flips to "premium" before we render /home.
                if (evt?.name === "checkout.completed") {
                    // GA4 purchase event (production-only). Value + currency
                    // let GA report revenue in built-in monetization reports.
                    track("purchase", {
                        transaction_id: evt?.data?.transaction_id || undefined,
                        value: isYearly ? 79.00 : 9.90,
                        currency: "CHF",
                        items: [{
                            item_id: isYearly ? "protection_annual" : "protection_monthly",
                            item_name: isYearly ? "PawLink Protection · Annual" : "PawLink Protection · Monthly",
                            price: isYearly ? 79.00 : 9.90,
                            quantity: 1,
                        }],
                    });
                    // TikTok Pixel — fires ONLY here, inside Paddle's
                    // checkout.completed callback, which means the payment
                    // succeeded. Never fired on overlay open, close, or on
                    // return-to-app without a confirmed transaction.
                    trackCompletePayment({
                        value: isYearly ? 79.00 : 9.90,
                        currency: "CHF",
                        orderId: evt?.data?.transaction_id || undefined,
                        contentId: isYearly ? "protection_annual" : "protection_monthly",
                        contentName: isYearly ? "PawLink Protection · Annual" : "PawLink Protection · Monthly",
                    });
                    // Guests aren't logged in yet — the webhook is provisioning their
                    // account in the background. Send them to /welcome with a
                    // "check your inbox" panel. Authenticated users refresh + go home.
                    const wasGuest = !user;
                    setTimeout(async () => {
                        if (wasGuest) {
                            setLoading(false);
                            nav("/welcome?paddle=success&guest=1");
                        } else {
                            try { await refresh(); } catch { /* noop */ }
                            setLoading(false);
                            nav("/home?paddle=success");
                        }
                    }, 2500);
                }
                if (evt?.name === "checkout.closed") {
                    setLoading(false);
                }
                // Paddle emits granular payment errors — including the "Enter a
                // valid name for the cardholder" validation. We capture the
                // exact reason so we can render a helpful hint (Paddle only
                // accepts letters, spaces, hyphens, and apostrophes).
                if (evt?.name === "checkout.payment.failed" || evt?.name === "checkout.error") {
                    const raw =
                        evt?.data?.error?.detail ||
                        evt?.data?.error?.message ||
                        evt?.data?.message ||
                        "";
                    if (/cardholder|name/i.test(raw)) {
                        setError(t("subscription.cardholder_hint"));
                    } else if (raw) {
                        setError(raw);
                    }
                }
            },
        }).then((p) => {
            paddleRef.current = p;
            setDiag((d) => {
                const next = d ? { ...d, paddleInitialized: true } : d;
                if (typeof window !== "undefined") window.__pawlink_paddle_diag = next;
                return next;
            });
        }).catch((err) => {
            // Paddle client init failed. Capture the message (safe — Paddle's
            // errors never include tokens) so ops can inspect via the diag panel.
            setDiag((d) => {
                const next = d ? { ...d, paddleInitialized: false, paddleInitError: String(err?.message || err).slice(0, 240) } : d;
                if (typeof window !== "undefined") window.__pawlink_paddle_diag = next;
                return next;
            });
        });
        })();  // end async IIFE

        return () => { cancelled = true; };
    }, [nav, refresh, isYearly, t, lang, user]);

    const startCheckout = async () => {
        if (loading || isPremium) return;
        // CHF launch: block paid checkout entirely when the new price IDs
        // aren't configured yet — the user sees the staging notice instead.
        if (checkoutDisabledReason) {
            setError(t(`subscription.${checkoutDisabledReason}`));
            return;
        }
        // Refuse to open Paddle if the production config guard tripped.
        if (configError) {
            setError(t(`subscription.${configError}`));
            return;
        }
        setError(""); setLoading(true);
        // Defensive pre-check: some Linux/embedded browsers report a non-BCP-47
        // navigator.language (e.g. "en-US@posix" when LC_ALL=C). Paddle's iframe
        // reads its own navigator.language and passes it straight into
        // Number.toLocaleString(), which throws "RangeError: Invalid language tag"
        // and crashes the overlay before the user can enter card details.
        // We cannot patch Paddle's cross-origin iframe, so we detect the bad
        // locale up front and surface a friendly message instead of the crash.
        try {
            new Intl.Locale(navigator.language || "en");
        } catch {
            setError(t("subscription.invalid_browser_locale"));
            setLoading(false);
            return;
        }
        // GA4 begin_checkout event — fires the moment the user commits to pay,
        // before Paddle overlay opens. Value/currency lets GA reconcile with
        // the eventual purchase event fired after checkout.completed.
        track("begin_checkout", {
            value: isYearly ? 79.00 : 9.90,
            currency: "CHF",
            items: [{
                item_id: isYearly ? "protection_annual" : "protection_monthly",
                item_name: isYearly ? "PawLink Protection · Annual" : "PawLink Protection · Monthly",
                price: isYearly ? 79.00 : 9.90,
                quantity: 1,
            }],
        });
        // TikTok Pixel — same "commits to pay" moment. Consent-gated: no-op
        // unless the user has explicitly granted analytics/marketing consent.
        trackInitiateCheckout({
            value: isYearly ? 79.00 : 9.90,
            currency: "CHF",
            contentId: isYearly ? "protection_annual" : "protection_monthly",
            contentName: isYearly ? "PawLink Protection · Annual" : "PawLink Protection · Monthly",
        });
        try {
            // Paddle path — preferred once REACT_APP_PADDLE_CLIENT_TOKEN is set.
            if (paddleRef.current) {
                // Two flows share one overlay:
                //   • Authenticated user  → /paddle/checkout — prefills email, locks it,
                //     custom_data.user_id ties the event to the existing PawLink account.
                //   • Anonymous visitor   → /paddle/checkout-guest — no prefill; Paddle
                //     collects the email in the widget. Webhook auto-provisions the
                //     PawLink account after payment and emails a magic sign-in link.
                const isGuest = !user;
                const path = isGuest ? "/paddle/checkout-guest" : "/paddle/checkout";
                const { data } = await api.post(path, {
                    plan: isYearly ? "yearly" : "monthly",
                });
                // Per Paddle docs (developer.paddle.com/paddle-js/methods/paddle-checkout-open)
                // pass EITHER customer.id (returning customer) OR customer.email
                // (new customer). Never both — Paddle throws "Unknown option".
                // Guests: no `customer` — Paddle prompts for email inside the overlay.
                const customer = isGuest
                    ? undefined
                    : (data.customer_id
                        ? { id: data.customer_id }
                        : (data.customer_email ? { email: data.customer_email } : undefined));
                // Success URL differs by flow: guests land on /welcome?paddle=success
                // with a "check your email" panel (they aren't signed in yet). Signed-in
                // users go straight to /home?paddle=success.
                const successUrl = isGuest
                    ? `${window.location.origin}/welcome?paddle=success&guest=1`
                    : `${window.location.origin}/home?paddle=success`;
                paddleRef.current.Checkout.open({
                    items: [{ priceId: data.price_id, quantity: 1 }],
                    customer,
                    customData: data.custom_data,
                    settings: {
                        displayMode: "overlay",
                        theme: "dark",
                        // Guests need to enter their email inside Paddle; they must be
                        // allowed to correct it if they typo it. Signed-in users have
                        // their email locked so they can't accidentally subscribe with
                        // the wrong account.
                        allowLogout: isGuest,
                        // BCP-47 locale — matches PawLink UI language and
                        // guards against Paddle auto-detecting an invalid
                        // POSIX locale that crashes the overlay.
                        locale: (lang || "en").split("-")[0],
                        successUrl,
                    },
                });
                // Overlay is now open — loading stays true until eventCallback fires closed/completed.
                return;
            }
            // Paddle not initialized (client token missing or blocked). Surface a
            // clear error to the user instead of silently falling back.
            throw new Error("checkout_unavailable");
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || t("subscription.checkout_error"));
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-paw-bone dark:bg-[hsl(152,15%,7%)] text-paw-ink dark:text-paw-bone overflow-x-clip"
            data-testid="sub-page"
        >
            <Nav />

            <main
                data-testid="sub-main"
                className="pt-16 sm:pt-20 lg:pt-24 pb-14 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
            >
                {/* Compact header — no long paragraph. */}
                <div className="text-center max-w-2xl mx-auto">
                    <p
                        data-testid="sub-kicker"
                        className="inline-flex items-center gap-2 rounded-full border border-paw-line dark:border-white/15 bg-white/70 dark:bg-white/[0.04] px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-semibold text-paw-ink dark:text-paw-bone/90"
                    >
                        <ShieldCheck size={11} weight="fill" className="text-paw-terra" />
                        {t("subscription.kicker_protection")}
                    </p>
                    <h1
                        data-testid="sub-page-title"
                        className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-paw-ink dark:text-paw-bone"
                    >
                        {t("subscription.page_title")}
                    </h1>
                    <p
                        data-testid="sub-page-sub"
                        className="mt-3 text-base sm:text-lg text-paw-ink2 dark:text-paw-bone/85"
                    >
                        {t("subscription.page_sub")}
                    </p>
                </div>

                {/* Cancel button appears above cards for existing subscribers. */}
                {isPremium && (
                    <div
                        data-testid="sub-active-chip"
                        className="mt-6 mx-auto max-w-md inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 px-5 py-3 text-sm font-semibold"
                    >
                        <ShieldCheck size={16} weight="fill" />
                        {t("subscription.emo_cta_premium")}
                    </div>
                )}

                {/* TWO CARDS — side-by-side on desktop, stacked on mobile. */}
                <div
                    data-testid="sub-plans-grid"
                    className="mt-8 sm:mt-10 grid gap-5 lg:gap-6 lg:grid-cols-2 lg:items-stretch"
                >
                    <FreePlanCard t={t} isStoreApp={isStoreApp} nav={nav} reduce={reduce} />
                    <ProtectionPlanCard
                        t={t}
                        isPremium={isPremium}
                        isStoreApp={isStoreApp}
                        billing={billing}
                        isYearly={isYearly}
                        setBilling={setBilling}
                        onCheckout={startCheckout}
                        loading={loading}
                        error={error}
                        configError={configError}
                        checkoutDisabledReason={checkoutDisabledReason}
                        reduce={reduce}
                    />
                </div>

                {isPremium && (
                    <div className="mt-6 max-w-md mx-auto">
                        <CancelSubscriptionButton t={t} />
                    </div>
                )}

                {/* Trust line — clear CHF pricing, no hidden fees. */}
                <p
                    data-testid="sub-trust-line"
                    className="mt-10 text-center text-[12px] sm:text-[13px] text-paw-ink2 dark:text-paw-bone/60"
                >
                    {t("subscription.trust_line")}
                </p>

                {/* Legal links — Paddle domain-approval requires Terms + Refund
                    + Privacy one click away from the checkout CTA. Kept on all
                    variants; policy links are never a purchase surface. */}
                <p
                    data-testid="sub-legal-links"
                    className="mt-3 text-center text-[11px] leading-relaxed text-paw-ink2/80 dark:text-paw-bone/45"
                >
                    <a href="/terms-of-use" className="underline hover:text-paw-terra" data-testid="sub-link-terms">Terms of Use</a>
                    <span aria-hidden> · </span>
                    <a href="/refund-policy" className="underline hover:text-paw-terra" data-testid="sub-link-refund">Refund Policy</a>
                    <span aria-hidden> · </span>
                    <a href="/privacy-policy" className="underline hover:text-paw-terra" data-testid="sub-link-privacy">Privacy Policy</a>
                </p>

                {/* Billing help */}
                <p
                    className="mt-4 text-center text-[11px] sm:text-xs text-paw-ink2 dark:text-paw-bone/55"
                    data-testid="sub-billing-help"
                >
                    {t("subscription.billing_help")}{" "}
                    <Link
                        to="/support?subject=Billing"
                        className="text-paw-forest dark:text-paw-terra hover:underline font-medium"
                        dir="ltr"
                    >
                        {SUPPORT_EMAIL}
                    </Link>
                </p>
            </main>
        </div>
    );
}

/* =====================================================================
 *  FREE PLAN CARD
 * ===================================================================== */
function FreePlanCard({ t, isStoreApp, nav, reduce }) {
    const features = [
        { title: t("subscription.free_feat_1_title"), sub: t("subscription.free_feat_1_sub") },
        { title: t("subscription.free_feat_2_title"), sub: t("subscription.free_feat_2_sub") },
        { title: t("subscription.free_feat_3_title"), sub: t("subscription.free_feat_3_sub") },
        { title: t("subscription.free_feat_4_title"), sub: t("subscription.free_feat_4_sub") },
    ];
    return (
        <motion.div
            initial={{ opacity: 0, y: reduce ? 0 : 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: easeOut }}
            data-testid="sub-plan-free-card"
            className="relative rounded-3xl border border-paw-line dark:border-white/10 bg-white/95 dark:bg-white/[0.04] p-6 sm:p-7 flex flex-col"
        >
            <div>
                <h2 className="font-display text-2xl sm:text-3xl text-paw-ink dark:text-paw-bone tracking-tight">
                    {t("subscription.free_name")}
                </h2>
                <p className="mt-2 text-[15px] text-paw-ink2 dark:text-paw-bone/85 leading-snug">
                    {t("subscription.free_tagline")}
                </p>
                <div className="mt-5 flex items-baseline gap-2">
                    <span data-testid="sub-plan-free-price" className="font-display text-4xl text-paw-ink dark:text-paw-bone" dir="ltr">
                        {t("subscription.free_price")}
                    </span>
                    <span className="text-sm text-paw-ink2 dark:text-paw-bone/80">
                        · {t("subscription.free_billing_label")}
                    </span>
                </div>
            </div>

            <ul className="mt-6 space-y-3.5 flex-1" data-testid="sub-plan-free-features">
                {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5" data-testid={`sub-free-feat-${i + 1}`}>
                        <PawPrint size={16} weight="fill" className="text-paw-forest dark:text-paw-terra mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                            <p className="text-[15px] font-semibold text-paw-ink dark:text-paw-bone leading-snug">{f.title}</p>
                            <p className="text-[14px] text-paw-ink2 dark:text-paw-bone/80 leading-snug">{f.sub}</p>
                        </div>
                    </li>
                ))}
            </ul>

            <button
                type="button"
                onClick={() => nav(isStoreApp ? "/home" : "/register")}
                data-testid="sub-free-cta"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full border-2 border-paw-forest dark:border-paw-terra text-paw-forest dark:text-paw-terra px-5 py-3 text-[15px] font-semibold hover:bg-paw-forest/8 dark:hover:bg-paw-terra/10 transition"
            >
                {isStoreApp ? t("subscription.free_cta_android") : t("subscription.free_cta_web")}
                <ArrowRight size={14} weight="bold" />
            </button>
        </motion.div>
    );
}

/* =====================================================================
 *  PROTECTION PLAN CARD
 * ===================================================================== */
function ProtectionPlanCard({ t, isPremium, isStoreApp, billing, isYearly, setBilling, onCheckout, loading, error, configError, checkoutDisabledReason, reduce }) {
    const features = [
        { title: t("subscription.pro_feat_1_title"),  sub: t("subscription.pro_feat_1_sub") },
        { title: t("subscription.pro_feat_2_title"),  sub: t("subscription.pro_feat_2_sub") },
        { title: t("subscription.pro_feat_3_title"),  sub: t("subscription.pro_feat_3_sub") },
        { title: t("subscription.pro_feat_4_title"),  sub: t("subscription.pro_feat_4_sub") },
        { title: t("subscription.pro_feat_5_title"),  sub: t("subscription.pro_feat_5_sub") },
        { title: t("subscription.pro_feat_6_title"),  sub: t("subscription.pro_feat_6_sub") },
        { title: t("subscription.pro_feat_7_title"),  sub: t("subscription.pro_feat_7_sub") },
        { title: t("subscription.pro_feat_8_title"),  sub: t("subscription.pro_feat_8_sub") },
        { title: t("subscription.pro_feat_9_title"),  sub: t("subscription.pro_feat_9_sub") },
        { title: t("subscription.pro_feat_10_title"), sub: t("subscription.pro_feat_10_sub") },
    ];
    return (
        <motion.div
            initial={{ opacity: 0, y: reduce ? 0 : 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: easeOut }}
            data-testid="sub-plan-protection-card"
            className="relative rounded-3xl border-2 border-paw-terra bg-gradient-to-br from-paw-terra/6 to-white/60 dark:from-paw-terra/12 dark:to-white/[0.02] p-6 sm:p-7 shadow-hover flex flex-col overflow-hidden"
        >
            {/* Recommended badge */}
            <span
                data-testid="sub-recommended-badge"
                className="absolute -top-3 start-6 inline-flex items-center gap-1.5 rounded-full bg-paw-terra text-white text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1.5 shadow-hover"
            >
                <Crown size={11} weight="fill" />
                {t("subscription.recommended_badge")}
            </span>
            <div aria-hidden className="absolute -top-24 -end-24 w-56 h-56 rounded-full bg-paw-terra/22 blur-3xl pointer-events-none" />

            <div className="relative">
                <h2 className="font-display text-2xl sm:text-3xl text-paw-ink dark:text-paw-bone tracking-tight">
                    {t("subscription.protection_name")}
                </h2>
                <p className="mt-2 text-[15px] text-paw-ink2 dark:text-paw-bone/85 leading-snug">
                    {t("subscription.protection_tagline")}
                </p>

                {/* Billing selector (Monthly / Annual) — Annual selected by default. */}
                <div
                    role="radiogroup"
                    aria-label={t("subscription.pick_a_plan_aria")}
                    data-testid="sub-billing-selector"
                    className="mt-5 grid grid-cols-2 gap-2 rounded-2xl border border-paw-line dark:border-white/10 bg-white/70 dark:bg-white/[0.03] p-1"
                >
                    <button
                        type="button"
                        role="radio"
                        aria-checked={!isYearly}
                        onClick={() => setBilling("monthly")}
                        data-testid="sub-billing-monthly"
                        data-selected={!isYearly}
                        className={
                            "rounded-xl px-3 py-2 text-[13px] font-semibold transition " +
                            (!isYearly
                                ? "bg-paw-terra text-white shadow-hover"
                                : "text-paw-ink dark:text-paw-bone/80 hover:bg-paw-terra/8")
                        }
                    >
                        {t("subscription.plan_monthly_label")}
                    </button>
                    <button
                        type="button"
                        role="radio"
                        aria-checked={isYearly}
                        onClick={() => setBilling("yearly")}
                        data-testid="sub-billing-annual"
                        data-selected={isYearly}
                        className={
                            "relative rounded-xl px-3 py-2 text-[13px] font-semibold transition " +
                            (isYearly
                                ? "bg-paw-terra text-white shadow-hover"
                                : "text-paw-ink dark:text-paw-bone/80 hover:bg-paw-terra/8")
                        }
                    >
                        {t("subscription.plan_yearly_label")}
                        <span
                            data-testid="sub-save-badge"
                            className={
                                "ms-1.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider " +
                                (isYearly
                                    ? "bg-white/25 text-white"
                                    : "bg-paw-terra text-white")
                            }
                        >
                            {t("subscription.save_33")}
                        </span>
                    </button>
                </div>

                {/* Price display */}
                <div className="mt-4">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={billing}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.25 }}
                            className="flex items-baseline gap-2"
                        >
                            <span
                                data-testid="sub-protection-price"
                                className="font-display text-4xl text-paw-ink dark:text-paw-bone"
                                dir="ltr"
                            >
                                {isYearly ? t("subscription.protection_price_annual") : t("subscription.protection_price_monthly")}
                            </span>
                            <span className="text-sm text-paw-ink2 dark:text-paw-bone/80">
                                {isYearly ? t("subscription.per_year") : t("subscription.per_month")}
                            </span>
                        </motion.div>
                    </AnimatePresence>
                    {isYearly && (
                        <p
                            data-testid="sub-annual-equivalent"
                            className="mt-1.5 text-[13px] text-paw-ink2 dark:text-paw-bone/80"
                        >
                            {t("subscription.annual_equivalent")}
                        </p>
                    )}
                </div>

                {/* Feature list */}
                <ul className="mt-5 space-y-3 flex-1" data-testid="sub-plan-protection-features">
                    {features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2.5" data-testid={`sub-pro-feat-${i + 1}`}>
                            <ShieldCheck size={16} weight="fill" className="text-paw-terra mt-0.5 flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="text-[15px] font-semibold text-paw-ink dark:text-paw-bone leading-snug">{f.title}</p>
                                <p className="text-[14px] text-paw-ink2 dark:text-paw-bone/80 leading-snug">{f.sub}</p>
                            </div>
                        </li>
                    ))}
                </ul>
                <p
                    data-testid="sub-cancel-anytime"
                    className="mt-4 flex items-center gap-1.5 text-[12px] text-paw-ink2 dark:text-paw-bone/55"
                >
                    <LockKey size={12} weight="fill" />
                    {t("subscription.cancel_anytime")}
                </p>

                {/* CTA area */}
                <div className="mt-5">
                    {/* Staging notice — only shown on web (not android) and not to premium users. */}
                    {checkoutDisabledReason && !configError && !isStoreApp && !isPremium && (
                        <div
                            data-testid="sub-checkout-awaiting"
                            className="mb-4 flex items-start gap-2 rounded-2xl border border-amber-300 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-[13px] leading-snug text-amber-900 dark:text-amber-100"
                        >
                            <WarningCircle size={16} weight="fill" className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                            <span>{t(`subscription.${checkoutDisabledReason}`)}</span>
                        </div>
                    )}

                    {/* Config-guard banner (production live-env misconfig) — always shown when tripped. */}
                    {configError && (
                        <div
                            data-testid="sub-config-error"
                            className="mb-4 flex items-start gap-2 rounded-2xl border border-red-300 dark:border-red-800/50 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-[13px] leading-snug text-red-800 dark:text-red-200"
                        >
                            <WarningCircle size={16} weight="fill" className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                            <span>{t(`subscription.${configError}`)}</span>
                        </div>
                    )}

                    <AnimatePresence>
                        {error && (
                            <motion.div
                                key="err"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.25 }}
                                data-testid="sub-checkout-error"
                                className="mb-3 flex items-start gap-2 rounded-2xl border border-red-200 dark:border-red-800/40 bg-red-50/70 dark:bg-red-950/30 px-3.5 py-2.5 text-sm text-red-800 dark:text-red-200"
                            >
                                <WarningCircle size={16} weight="fill" className="text-red-600 mt-0.5 flex-shrink-0" />
                                <span>{error}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {isPremium ? (
                        <p
                            data-testid="sub-premium-active-line"
                            className="text-center text-[13px] text-emerald-700 dark:text-emerald-300"
                        >
                            {t("subscription.premium_active_line")}
                        </p>
                    ) : isStoreApp ? (
                        <StoreAppProtectionArea t={t} />
                    ) : (
                        <button
                            type="button"
                            onClick={onCheckout}
                            disabled={loading || !!configError || !!checkoutDisabledReason}
                            aria-busy={loading}
                            data-testid="sub-cta-checkout"
                            className="group/cta relative w-full inline-flex items-center justify-center gap-2 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-5 py-3.5 text-base font-semibold shadow-hover overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 hover:-translate-y-0.5"
                        >
                            {loading ? (
                                <>
                                    <span aria-hidden className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                                    <span>{t("subscription.redirecting")}</span>
                                </>
                            ) : (
                                <>
                                    <Sparkle size={16} weight="fill" />
                                    <span data-testid="sub-cta-label">
                                        {isYearly ? t("subscription.cta_choose_annual") : t("subscription.cta_choose_monthly")}
                                    </span>
                                    <ArrowRight size={14} weight="bold" className="transition-transform group-hover/cta:translate-x-0.5 rtl:group-hover/cta:-translate-x-0.5" />
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </motion.div>
    );
}

/* =====================================================================
 *  STORE-APP PROTECTION AREA (Android — no purchase surface)
 *  Renders plain, non-clickable info text + the four backend-only
 *  account buttons (Continue with Free / Sign In / I Already Subscribed
 *  / Refresh Subscription). Never opens Paddle, an external URL, or a
 *  clickable purchase link.
 * ===================================================================== */
function StoreAppProtectionArea({ t }) {
    return (
        <div data-testid="sub-android-protection-area" className="space-y-3">
            <p
                data-testid="sub-android-info"
                className="text-center text-[13px] leading-relaxed text-paw-ink2 dark:text-paw-bone/70"
            >
                {t("subscription.android_info_text")}
            </p>
            <AndroidAccountActions t={t} />
        </div>
    );
}

/* Cancel-subscription control shown only when the user is currently premium.
   Confirms once, then calls POST /api/paddle/cancel — server cancels via
   Paddle at the end of the current billing period. Webhook flips plan back
   to "free" automatically when that period ends. */
function CancelSubscriptionButton({ t }) {
    const [busy, setBusy] = useState(false);
    const [ok, setOk] = useState("");
    const [err, setErr] = useState("");
    const [confirm, setConfirm] = useState(false);

    const doCancel = async () => {
        if (busy) return;
        setBusy(true); setErr(""); setOk("");
        try {
            await api.post("/paddle/cancel");
            setOk(t("subscription.cancel_ok"));
            setConfirm(false);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || t("subscription.cancel_error"));
        } finally {
            setBusy(false);
        }
    };

    if (ok) {
        return (
            <p
                data-testid="sub-cancel-success"
                className="mt-3 text-center text-xs text-emerald-700 dark:text-emerald-300"
            >
                {ok}
            </p>
        );
    }

    return (
        <div className="mt-3 text-center">
            {!confirm ? (
                <button
                    type="button"
                    onClick={() => setConfirm(true)}
                    data-testid="sub-cancel-open"
                    className="text-xs text-paw-ink2 dark:text-paw-bone/60 hover:text-red-600 dark:hover:text-red-400 underline underline-offset-4"
                >
                    {t("subscription.cancel_manage")}
                </button>
            ) : (
                <div className="flex items-center justify-center gap-2">
                    <button
                        type="button"
                        onClick={doCancel}
                        disabled={busy}
                        data-testid="sub-cancel-confirm"
                        className="text-xs rounded-full bg-red-500/12 border border-red-500/30 text-red-700 dark:text-red-300 px-3 py-1.5 hover:bg-red-500/20 transition disabled:opacity-70"
                    >
                        {busy ? t("subscription.canceling") : t("subscription.cancel_confirm")}
                    </button>
                    <button
                        type="button"
                        onClick={() => setConfirm(false)}
                        disabled={busy}
                        data-testid="sub-cancel-back"
                        className="text-xs text-paw-ink2 dark:text-paw-bone/60 hover:underline"
                    >
                        {t("subscription.cancel_back")}
                    </button>
                </div>
            )}
            {err && (
                <p data-testid="sub-cancel-error" className="mt-2 text-[11px] text-red-600 dark:text-red-400">{err}</p>
            )}
        </div>
    );
}


/* AndroidAccountActions — three backend-only buttons for the Google Play
   build. None of them open a purchase surface, an external URL, an
   embedded browser, or a deep link that could reach Paddle. Their sole
   purpose is to let existing subscribers unlock their entitlement after
   subscribing via the web. Purchase itself only happens on getpawlink.com. */
function AndroidAccountActions({ t }) {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);
    const [ok, setOk] = useState("");
    const [err, setErr] = useState("");

    const onRefresh = async () => {
        if (busy) return;
        setBusy(true); setErr(""); setOk("");
        try {
            // Re-reads users.plan from backend (source of truth). If the
            // web-side Paddle webhook has activated Protection, this call
            // flips the local session and unlocks Protection features.
            await refresh();
            setOk(t("subscription.android_refresh_ok"));
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || t("subscription.android_refresh_err"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-4 grid grid-cols-1 gap-2" data-testid="sub-android-actions">
            {user ? (
                <>
                    <button
                        type="button"
                        onClick={onRefresh}
                        disabled={busy}
                        data-testid="sub-android-refresh"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-paw-terra/40 bg-white/60 dark:bg-white/[0.05] text-paw-ink dark:text-paw-bone px-4 py-2.5 text-sm font-medium hover:bg-paw-terra/10 disabled:opacity-60"
                    >
                        {busy ? t("subscription.android_refreshing") : t("subscription.android_refresh")}
                    </button>
                    <p
                        data-testid="sub-android-already"
                        className="mt-1 text-center text-[12px] text-paw-ink2 dark:text-paw-bone/60"
                    >
                        {t("subscription.android_already_subscribed")}
                    </p>
                    {ok && <p data-testid="sub-android-ok" className="text-center text-[12px] text-emerald-700 dark:text-emerald-300">{ok}</p>}
                    {err && <p data-testid="sub-android-err" className="text-center text-[12px] text-red-600 dark:text-red-400">{err}</p>}
                </>
            ) : (
                <button
                    type="button"
                    onClick={() => nav("/login")}
                    data-testid="sub-android-signin"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-paw-terra text-white px-4 py-2.5 text-sm font-semibold hover:bg-paw-terraDark"
                >
                    {t("subscription.android_signin")}
                </button>
            )}
        </div>
    );
}
